import React from "react";
import { Pressable, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import Text from "@/components/shared/AppText";
import { openSupport, useSupportStatus } from "@/components/support/config";
import { t, tc } from "@/localization";
import { bytesToSize } from "../globalUtils";
import { CONVERSION_FAILED_MESSAGE } from "./manager";

const PLACEMENT = {
  replace: t("It replaces the event's video."),
  append: t("It goes at the end of the event's video."),
  prepend: t("It goes at the beginning of the event's video."),
  insert: t("It goes between the event's video fragments."),
};

const LinkButton = ({ text, onPress, muted, disabled }) => (
  <Pressable
    accessibilityRole="button"
    accessibilityState={{ disabled: Boolean(disabled) }}
    disabled={disabled}
    onPress={onPress}
    style={tw`mr-4 py-1`}
  >
    <Text
      style={[
        tw`font-bold`,
        muted || disabled ? tw`text-[#6f6f6f]` : tw`text-[#c74733]`,
      ]}
    >
      {text}
    </Text>
  </Pressable>
);

// Manage Video (web and native): an unfinished upload remembered on this
// device (choose the same file to continue) or a conversion evt.live couldn't
// finish (Try again queues the uploaded file again). Renders nothing
// otherwise. `onResume` asks for the file; `children` go inside the resume
// offer (the web's hidden file input).
const UploadV2NoticeView = ({
  uploadV2,
  onResume,
  onDiscard,
  pickError,
  style,
  children,
}) => {
  const { available: supportAvailable } = useSupportStatus();
  const { state } = uploadV2;

  if (!uploadV2.available) return null;
  const offer = state.status === "idle" ? state.resumable : null;
  const conversionFailed =
    state.status === "failed" && state.errorStage === "conversion";
  if (!offer && !conversionFailed) return null;

  if (conversionFailed) {
    // evt.live's errorMessage says why and what to do (its "We couldn't
    // process this video." repeats the title); without one, say what to do
    // here, Try again only when evt.live allows it.
    const detail =
      state.error && state.error !== CONVERSION_FAILED_MESSAGE
        ? state.error.replace(CONVERSION_FAILED_MESSAGE, "").trim()
        : "";
    const nextStep =
      detail ||
      (state.retryable !== false
        ? t("Try again, or upload it again.")
        : t("Please upload it again."));
    return (
      <View
        style={[tw`mb-4 p-4 rounded-xl border border-red-200 bg-red-50`, style]}
      >
        <View style={tw`flex-row items-start`}>
          <Ionicons
            name="alert-circle"
            size={20}
            color="#c74733"
            style={tw`mr-2 mt-0.5`}
          />
          <View style={tw`flex-1`}>
            <Text style={tw`font-bold text-base`}>
              {t("We couldn't process your video")}
            </Text>
            <Text style={tw`text-gray-700 mt-1 leading-5`}>
              {state.filename
                ? t("“{filename}” was uploaded but couldn't be converted.", {
                    filename: state.filename,
                  })
                : t("The video was uploaded but couldn't be converted.")}{" "}
              {nextStep} {t("If it keeps happening, contact support.")}
            </Text>
            {!!state.retryError && (
              <Text style={tw`mt-1 text-[#c74733] leading-5`}>
                {state.retryError}
              </Text>
            )}
            <View style={tw`flex-row flex-wrap mt-2`}>
              {state.retryable !== false && (
                <LinkButton
                  text={state.retrying ? t("Trying again…") : t("Try again →")}
                  disabled={state.retrying}
                  onPress={() => uploadV2.retryConversion()}
                />
              )}
              {supportAvailable && (
                <LinkButton
                  text={t("Contact support →")}
                  onPress={openSupport}
                  muted
                />
              )}
            </View>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={tc("notice", "Dismiss")}
            onPress={uploadV2.dismiss}
            hitSlop={10}
            style={tw`ml-2`}
          >
            <Ionicons name="close" size={20} color="#8E8E93" />
          </Pressable>
        </View>
      </View>
    );
  }

  const percent = offer.size
    ? Math.floor((offer.uploadedBytes / offer.size) * 100)
    : 0;
  const filename = offer.filename ?? "";
  const size = bytesToSize(offer.size);

  return (
    <View
      style={[
        tw`mb-4 p-4 rounded-xl border border-orange-200 bg-orange-50`,
        style,
      ]}
    >
      <View style={tw`flex-row items-start`}>
        <Ionicons
          name="cloud-upload"
          size={20}
          color="#c74733"
          style={tw`mr-2 mt-0.5`}
        />
        <View style={tw`flex-1`}>
          <Text style={tw`font-bold text-base`}>{t("Unfinished upload")}</Text>
          <Text style={tw`text-gray-700 mt-1 leading-5`}>
            {percent > 0
              ? t("“{filename}” ({size}), {percent}% uploaded.", {
                  filename,
                  size,
                  percent,
                })
              : `“${filename}” (${size}).`}{" "}
            {PLACEMENT[offer.mode] || ""}{" "}
            {t("Choose the same file to continue where the upload stopped.")}
          </Text>
          {!!pickError && (
            <Text style={tw`mt-1 text-[#c74733] leading-5`}>{pickError}</Text>
          )}
          <View style={tw`flex-row mt-2`}>
            <LinkButton text={t("Resume upload →")} onPress={onResume} />
            <LinkButton text={t("Discard")} onPress={onDiscard} muted />
          </View>
          {children}
        </View>
      </View>
    </View>
  );
};

export default UploadV2NoticeView;
